import { useEffect } from 'react';
import { useState } from 'react';
import { Cpu, AlertCircle, ChevronDown, ChevronRight, FlaskConical, BookOpen } from 'lucide-react';
import { customInstance } from '@edison/api-client-react';
import { Badge } from '@/components/ui/badge.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';

interface TimeEntryForPanel {
  id: string;
  startTime: string;
  endTime: string;
  reviewStatus: string;
  source?: string;
  rdPercentage: string | null;
  metadata: { repoOwner?: string; repoName?: string; commitMessages?: string[] } | null;
  classification: {
    label: string;
    confidence: string;
    rdPercentage: string;
    rationale: string | null;
  } | null;
}

interface TimelineData {
  session: {
    id: string;
    project: string;
    createdAt: string;
    scriptHash: string | null;
    summary: {
      technicalProblem: string;
      approachTaken: string;
      filesContext: string;
      rdRelevance: 'high' | 'medium' | 'low' | 'none';
      sessionSummary: string;
    } | null;
  } | null;
  events: Array<{ id: string; ts: string; tool: string; file: string | null }>;
  source: string;
}

interface WorkBucket {
  id: string;
  label: string;
  aiColumn: string;
  column: string;
  weight: string;
  rationale: string;
  tfaMatch: boolean;
  tfaAspect: string | null;
}

function formatRelativeTime(sessionStart: string, eventTs: string): string {
  const diff = new Date(eventTs).getTime() - new Date(sessionStart).getTime();
  const totalSeconds = Math.floor(diff / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function rdRelevanceBadgeVariant(rdRelevance: string): 'positive' | 'info' | 'default' | 'warning' {
  if (rdRelevance === 'high') return 'positive';
  if (rdRelevance === 'medium') return 'info';
  if (rdRelevance === 'low') return 'default';
  return 'warning';
}

function MinuteGroup({ minute, events, sessionStart }: { minute: string; events: TimelineData['events']; sessionStart: string }) {
  const [expanded, setExpanded] = useState(false);
  const label = `${events.length} event${events.length !== 1 ? 's' : ''} at ${formatRelativeTime(sessionStart, minute + ':00')}`;
  return (
    <div className="border-b border-muted/50 last:border-0">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-2 py-1.5 text-xs text-muted-foreground w-full hover:text-foreground"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {label}
      </button>
      {expanded && (
        <ul className="ml-4 space-y-1 pb-1">
          {events.map((ev) => (
            <li key={ev.id} className="flex items-center gap-3 py-1 text-xs">
              <span className="font-mono text-muted-foreground w-16 shrink-0 tabular-nums">
                {formatRelativeTime(sessionStart, ev.ts)}
              </span>
              <Badge variant={ev.tool === 'Write' ? 'positive' : ev.tool === 'Edit' ? 'info' : 'default'} className="shrink-0 text-xs">
                {ev.tool}
              </Badge>
              <span className="truncate text-muted-foreground font-mono">{ev.file ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventGroup({ events, sessionStart }: { events: TimelineData['events']; sessionStart: string }) {
  const GROUP_THRESHOLD = 20;
  if (events.length <= GROUP_THRESHOLD) {
    return (
      <ul className="space-y-1">
        {events.map((ev) => (
          <li key={ev.id} className="flex items-center gap-3 py-1.5 text-xs border-b border-muted/50 last:border-0">
            <span className="font-mono text-muted-foreground w-16 shrink-0 tabular-nums">
              {formatRelativeTime(sessionStart, ev.ts)}
            </span>
            <Badge variant={ev.tool === 'Write' ? 'positive' : ev.tool === 'Edit' ? 'info' : 'default'} className="shrink-0 text-xs">
              {ev.tool}
            </Badge>
            <span className="truncate text-muted-foreground font-mono">{ev.file ?? '—'}</span>
          </li>
        ))}
      </ul>
    );
  }

  const byMinute = new Map<string, typeof events>();
  for (const ev of events) {
    const minute = ev.ts.slice(0, 16);
    const bucket = byMinute.get(minute) ?? [];
    bucket.push(ev);
    byMinute.set(minute, bucket);
  }

  return (
    <div className="space-y-1">
      {[...byMinute.entries()].map(([minute, group]) => (
        <MinuteGroup key={minute} minute={minute} events={group} sessionStart={sessionStart} />
      ))}
    </div>
  );
}

interface TimeEntryDetailPanelProps {
  entry: TimeEntryForPanel;
  open: boolean;
  onClose: () => void;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2 pt-1 border-t">
      {children}
    </p>
  );
}

export function TimeEntryDetailPanel({ entry, open, onClose }: TimeEntryDetailPanelProps) {
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(false);
  const [buckets, setBuckets] = useState<WorkBucket[] | null>(null);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsError, setBucketsError] = useState(false);

  const isCC = (entry.source ?? 'github') === 'claude_code';

  // Load all data when the panel opens (or when entry changes while open)
  useEffect(() => {
    if (!open) {
      setTimelineData(null);
      setTimelineError(false);
      setTimelineLoading(false);
      setBuckets(null);
      setBucketsError(false);
      setBucketsLoading(false);
      return;
    }

    if (isCC) {
      setTimelineLoading(true);
      setTimelineError(false);
      customInstance<{ data: TimelineData }>({ url: `/api/time-entries/${entry.id}/timeline`, method: 'GET' })
        .then((res) => setTimelineData(res.data))
        .catch(() => setTimelineError(true))
        .finally(() => setTimelineLoading(false));
    }

    if (entry.classification) {
      setBucketsLoading(true);
      setBucketsError(false);
      customInstance<{ data: { buckets: WorkBucket[] } }>({ url: `/api/time-entries/${entry.id}/buckets`, method: 'GET' })
        .then((res) => setBuckets(res.data.buckets))
        .catch(() => setBucketsError(true))
        .finally(() => setBucketsLoading(false));
    }
  }, [open, entry.id, isCC, entry.classification !== null]);

  const startDate = new Date(entry.startTime).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const startTime = new Date(entry.startTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(entry.endTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  const durationMs = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime();
  const durationH = Math.floor(durationMs / 3_600_000);
  const durationM = Math.floor((durationMs % 3_600_000) / 60_000);

  return (
    <SlidePanel open={open} onClose={onClose} title="Time Entry">
      <div className="px-5 py-4 space-y-4">

        {/* ── Details ── */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Date</p>
              <p className="text-sm">{startDate}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Duration</p>
              <p className="text-sm">{startTime}–{endTime} ({durationH > 0 ? `${durationH}h ` : ''}{durationM}m)</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Source</p>
              <Badge variant={isCC ? 'info' : 'default'}>
                {isCC ? 'Claude Code' : 'GitHub'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Status</p>
              <Badge variant={entry.reviewStatus === 'approved' ? 'positive' : entry.reviewStatus === 'rejected' ? 'destructive' : 'default'}>
                {entry.reviewStatus}
              </Badge>
            </div>
          </div>
          {entry.metadata?.repoOwner && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Repository</p>
              <p className="text-sm font-mono">{entry.metadata.repoOwner}/{entry.metadata.repoName}</p>
            </div>
          )}
        </div>

        {/* ── Commits ── */}
        {entry.metadata?.commitMessages && entry.metadata.commitMessages.length > 0 && (
          <div>
            <SectionHeading>Commits</SectionHeading>
            <ul className="space-y-1">
              {entry.metadata.commitMessages.slice(0, 8).map((msg, i) => (
                <li key={i} className="text-xs text-muted-foreground border-l-2 border-muted pl-2">{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Classification ── */}
        {entry.classification && (
          <div>
            <SectionHeading>Classification</SectionHeading>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge variant={entry.classification.label === 'rd' ? 'positive' : entry.classification.label === 'non_rd' ? 'default' : 'info'}>
                  {entry.classification.label === 'rd' ? 'R&D' : entry.classification.label === 'non_rd' ? 'Non-R&D' : 'Mixed'}
                </Badge>
                <span className="text-sm font-mono tabular-nums">{parseFloat(entry.classification.rdPercentage).toFixed(0)}% R&D</span>
                <span className={`text-sm font-mono tabular-nums font-medium ${parseFloat(entry.classification.confidence) > 85 ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {parseFloat(entry.classification.confidence).toFixed(0)}% confidence
                </span>
              </div>
              {entry.classification.rationale && (
                <p className="text-sm text-muted-foreground leading-relaxed">{entry.classification.rationale}</p>
              )}
            </div>
          </div>
        )}

        {/* ── Work breakdown ── */}
        {entry.classification && (
          <div>
            <SectionHeading>Work breakdown</SectionHeading>
            {bucketsLoading && (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}
              </div>
            )}
            {bucketsError && (
              <p className="text-xs text-muted-foreground">Could not load breakdown.</p>
            )}
            {!bucketsLoading && !bucketsError && buckets !== null && buckets.length === 0 && (
              <p className="text-xs text-muted-foreground">No bucket detail available for this entry.</p>
            )}
            {!bucketsLoading && !bucketsError && buckets !== null && buckets.length > 0 && (
              <div className="space-y-2">
                {buckets.map((b) => {
                  const pct = Math.round(parseFloat(b.weight) * 100);
                  const isRd = b.column === 'rd';
                  return (
                    <div key={b.id} className="rounded-lg border p-3 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium leading-tight">{b.label}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant={isRd ? 'positive' : 'default'} className="text-xs">
                            {isRd ? (
                              <><FlaskConical className="h-3 w-3 mr-1" aria-hidden="true" />R&D</>
                            ) : (
                              <><BookOpen className="h-3 w-3 mr-1" aria-hidden="true" />Non-R&D</>
                            )}
                          </Badge>
                          <span className="text-xs font-mono text-muted-foreground tabular-nums w-10 text-right">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-1 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${isRd ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{b.rationale}</p>
                      {b.tfaAspect && (
                        <p className="text-xs text-indigo-600 font-medium">TFA: {b.tfaAspect}</p>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground pt-1">
                  R&D% = weighted sum of R&D buckets · {buckets.length} bucket{buckets.length !== 1 ? 's' : ''} total
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Timeline (CC only) ── */}
        {isCC && (
          <div>
            <SectionHeading>Session timeline</SectionHeading>
            {timelineLoading && (
              <div className="space-y-3">
                <div className="h-24 rounded-lg bg-muted animate-pulse" />
                {[...Array(5)].map((_, i) => <div key={i} className="h-7 rounded bg-muted animate-pulse" />)}
              </div>
            )}

            {timelineError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                Failed to load timeline.
              </div>
            )}

            {!timelineLoading && !timelineError && timelineData && (
              <>
                {timelineData.session?.summary ? (
                  <div className="rounded-lg border p-4 space-y-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-indigo-500" aria-hidden="true" />
                      <span className="text-sm font-medium">Session Summary</span>
                      <Badge variant={rdRelevanceBadgeVariant(timelineData.session.summary.rdRelevance)}>
                        {timelineData.session.summary.rdRelevance} R&D relevance
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {timelineData.session.summary.sessionSummary}
                    </p>
                    <details>
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Technical problem</summary>
                      <p className="mt-2 text-xs text-muted-foreground">{timelineData.session.summary.technicalProblem}</p>
                    </details>
                    <details>
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Approach taken</summary>
                      <p className="mt-2 text-xs text-muted-foreground">{timelineData.session.summary.approachTaken}</p>
                    </details>
                  </div>
                ) : timelineData.session ? (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground mb-3">
                    Session available but no analysis yet.
                  </div>
                ) : null}

                {timelineData.events.length > 0 ? (
                  <EventGroup
                    events={timelineData.events}
                    sessionStart={timelineData.session?.createdAt ?? entry.startTime}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No file-edit heartbeats were recorded for this session.
                  </p>
                )}

                <p className="text-xs text-muted-foreground border-t pt-3 mt-3">
                  Evidence captured by Edison heartbeat · {timelineData.events.length} file event{timelineData.events.length !== 1 ? 's' : ''}
                </p>
              </>
            )}

            {!timelineLoading && !timelineError && !timelineData && (
              <p className="text-sm text-muted-foreground">No timeline data available.</p>
            )}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}
